// Tiny non-blocking banner that asks once "audio offline mitnehmen?".
// Renders only when:
//   - the offline flag is enabled
//   - the song actually has audio attachments
//   - the user has not yet answered for this song
//
// On 'Ja' the audio is fetched into the cache in the background.
// On 'Nein' the choice is persisted so we never ask again.
// On 'X' (close) the banner just disappears for this session.

import { useEffect, useState } from 'react'
import { OFFLINE_ENABLED } from '../../config/offline'
import {
  cacheSongAudio,
  dismissAudioCache,
  getAudioAttachments,
  getAudioBytes,
} from '../../lib/offline/autoCache'
import { getAttachmentPrefs } from '../../lib/offline/songCache'
import { formatBytes } from '../../lib/offline/quota'
import type { Song } from '../../types/songbook'

interface AudioCachePromptProps {
  song: Song
}

type Phase = 'hidden' | 'ask' | 'caching' | 'done'

export function AudioCachePrompt({ song }: AudioCachePromptProps) {
  const [phase, setPhase] = useState<Phase>('hidden')

  const audioFiles = getAudioAttachments(song)
  const audioBytes = getAudioBytes(song)

  useEffect(() => {
    if (!OFFLINE_ENABLED) return
    if (audioFiles.length === 0) return

    let cancelled = false

    getAttachmentPrefs(song.id).then((prefs) => {
      if (cancelled) return
      if (prefs === null) {
        setPhase('ask')
      }
    })

    return () => {
      cancelled = true
    }
  }, [song.id, audioFiles.length])

  if (phase === 'hidden') return null

  async function handleYes() {
    setPhase('caching')
    try {
      await cacheSongAudio(song)
      setPhase('done')
      setTimeout(() => setPhase('hidden'), 2000)
    } catch (err) {
      console.error('[offline] audio cache failed:', err)
      setPhase('hidden')
    }
  }

  async function handleNo() {
    await dismissAudioCache(song.id)
    setPhase('hidden')
  }

  return (
    <div className="mb-2 flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-xs text-text">
      {phase === 'ask' && (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-primary"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span className="flex-1">
            Audio-Dateien ({audioFiles.length} · {formatBytes(audioBytes)}) auch offline mitnehmen?
          </span>
          <button
            className="rounded bg-primary px-2 py-1 font-medium text-white active:bg-primary-dark"
            onClick={handleYes}
          >
            Ja
          </button>
          <button
            className="rounded border border-surface-lighter px-2 py-1 text-text-muted active:bg-surface-lighter"
            onClick={handleNo}
          >
            Nein
          </button>
        </>
      )}
      {phase === 'caching' && (
        <>
          <span className="inline-block animate-spin">⟳</span>
          <span className="flex-1">Audio wird heruntergeladen...</span>
        </>
      )}
      {phase === 'done' && (
        <span className="text-success">Audio offline verfuegbar</span>
      )}
    </div>
  )
}
